import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DollarSign, TrendingUp, Calendar, ArrowRightLeft } from "lucide-react";
import { format } from "date-fns";
import { da } from "date-fns/locale";

interface StripeBalanceWidgetProps {
  userId: string;
}

export default function StripeBalanceWidget({ userId }: StripeBalanceWidgetProps) {
  const { data, isLoading } = useQuery({
    queryKey: ["stripeBalance", userId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("get-stripe-balance");
      if (error) throw error;
      return data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Stripe Balance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Henter balance...</p>
        </CardContent>
      </Card>
    );
  }

  if (!data?.hasAccount) {
    return null;
  }

  const availableAmount = data.balance?.available?.[0]?.amount || 0;
  const pendingAmount = data.balance?.pending?.[0]?.amount || 0;
  const currency = data.balance?.currency || "dkk";

  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const formatDate = (timestamp: number) => {
    return format(new Date(timestamp * 1000), "d. MMM yyyy", { locale: da });
  };

  return (
    <div className="space-y-4">
      {/* Balance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tilgængelig balance</p>
                <p className="text-2xl font-bold">
                  {formatAmount(availableAmount)} {currency.toUpperCase()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Klar til udbetaling
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Afventende</p>
                <p className="text-2xl font-bold">
                  {formatAmount(pendingAmount)} {currency.toUpperCase()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Behandles
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout Schedule Info */}
      {data.payoutSchedule && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium">Udbetalingsplan</p>
                <p className="text-sm text-muted-foreground">
                  {data.payoutSchedule.interval === "manual" 
                    ? "Manuel udbetaling - du skal selv initiere udbetalinger"
                    : `Automatisk udbetaling hver ${data.payoutSchedule.interval === "daily" ? "dag" : data.payoutSchedule.interval === "weekly" ? "uge" : "måned"}`
                  }
                </p>
                {data.payoutSchedule.delay_days > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Forsinkelse: {data.payoutSchedule.delay_days} dage
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transfers */}
      {data.transfers && data.transfers.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" />
              <CardTitle>Seneste overførsler</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.transfers.map((transfer: any) => (
                <div key={transfer.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {transfer.description || "Platform overførsel"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(transfer.created)}
                    </p>
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {formatAmount(transfer.amount)} {transfer.currency.toUpperCase()}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Payout */}
      {data.nextPayout && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg mt-0.5">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Næste udbetaling</p>
                <p className="text-xl font-bold">
                  {formatAmount(data.nextPayout.amount)} {data.nextPayout.currency.toUpperCase()}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Forventes: {formatDate(data.nextPayout.arrival_date)}
                </p>
                <Badge variant="secondary" className="mt-2">
                  {data.nextPayout.status === "pending" ? "Afventer" : data.nextPayout.status}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
